/**
 * Renders a read-only phone number as a tappable tel: link on mobile,
 * or plain text on desktop. Used for patient/physician contact displays.
 *
 * - Mobile (<768px): valid 10-digit US phones render as <a href="tel:+1...">
 * - Desktop: plain text, no link
 * - Invalid/partial numbers: plain text (no broken links)
 */
import type { MouseEventHandler } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { formatDialablePhoneText, toDialablePhone } from '../utils/phoneLink';

interface PhoneLinkProps {
  phone: string | null | undefined;
  fallback?: string;
  className?: string;
  /**
   * Applied to the tel: anchor only, never to the plain-text renderings. Lets a
   * caller inside a clickable container keep placing a call from also
   * triggering that container, without swallowing clicks on text that is not a
   * link.
   */
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

export function PhoneLink({
  phone,
  fallback = 'N/A',
  className,
  onClick,
}: PhoneLinkProps) {
  const isMobile = useIsMobile();
  const trimmed = phone?.trim();

  if (!trimmed) {
    return <span className={className}>{fallback}</span>;
  }

  const dialablePhone = toDialablePhone(trimmed);

  if (!isMobile || !dialablePhone) {
    return <span className={className}>{trimmed}</span>;
  }

  return (
    <a className={className} href={`tel:${dialablePhone}`} onClick={onClick}>
      {formatDialablePhoneText(trimmed)}
    </a>
  );
}
